import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  clientId?: string; // For client-based tenants
}

interface TenantContextType {
  tenant: Tenant | null;
  setTenant: (tenant: Tenant) => void;
  currentTenantId: string | null;
  setCurrentTenantId: (id: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);

  // Load tenant from localStorage or URL on initial load
  useEffect(() => {
    // Get tenant from subdomain or localStorage
    const subdomain = window.location.hostname.split('.')[0];
    if (subdomain && subdomain !== 'localhost' && subdomain !== 'www') {
      // In a real app, you would fetch tenant details based on subdomain
      const mockTenant: Tenant = {
        id: subdomain,
        name: `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)} Healthcare`,
        clientId: 'client-1', // Default client ID for demo
      };
      setTenant(mockTenant);
      setCurrentTenantId(subdomain);
    } else {
      // Check for tenant in localStorage (for demo purposes)
      const savedTenantId = localStorage.getItem('currentTenantId');
      if (savedTenantId) {
        const mockTenant: Tenant = {
          id: savedTenantId,
          name: `${savedTenantId.charAt(0).toUpperCase() + savedTenantId.slice(1)} Healthcare`,
          clientId: 'client-1', // Default client ID for demo
        };
        setTenant(mockTenant);
        setCurrentTenantId(savedTenantId);
      }
    }
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, setTenant, currentTenantId, setCurrentTenantId }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};